import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ArrowRight, MoonStar, SunMedium } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import api from '../api';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [microsoftEnabled, setMicrosoftEnabled] = useState(null); // null = unknown, hides button until known
  const { login, register } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    let active = true;
    api.get('/auth/config')
      .then((res) => { if (active) setMicrosoftEnabled(Boolean(res.data?.microsoftEnabled)); })
      .catch(() => { if (active) setMicrosoftEnabled(false); });
    return () => { active = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        setError(detail || 'Too many attempts — please wait a few minutes before trying again.');
      } else if (status === 403) {
        setError(detail || 'This email domain is not allowed.');
      } else {
        setError(Array.isArray(detail) ? detail.map((entry) => entry.msg || 'Invalid input').join(' ') : detail || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setMsLoading(true);
    setError('');
    try {
      const res = await api.get('/auth/microsoft/login');
      const { loginUrl } = res.data;
      window.location.href = loginUrl;
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not initiate Microsoft login');
      setMsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row" data-testid="login-page" style={{ background: 'var(--pd-bg)' }}>
      {/* ── Left · editorial hero (pure typography, spatial breathing room) ── */}
      <div className="relative w-full lg:w-[60%] flex flex-col justify-between px-8 py-8 md:px-14 md:py-12 lg:px-20 lg:py-16 min-h-[38vh] lg:min-h-screen overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'var(--pd-hero-wash)' }} />
        <div className="pd-noise absolute inset-0 pointer-events-none" />

        <div className="relative z-10 flex items-center gap-3 login-hero-reveal">
          <BrandMark variant="mark" size="md" testId="login-brand-mark" />
          <span className="pd-brand-wordmark" style={{ fontSize: 17 }}>
            PolkDes<span className="pd-brand-wordmark-q">Q</span>
          </span>
        </div>

        <div className="relative z-10 my-10 lg:my-0">
          <div className="text-[10px] uppercase tracking-[0.3em] login-hero-reveal login-hero-reveal-d1" style={{ color: 'var(--pd-muted)' }}>
            श्रम एव जयते · śrama eva jayate
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-[4.75rem] font-light mt-6 max-w-2xl login-hero-reveal login-hero-reveal-d2"
            style={{ color: 'var(--pd-text)', letterSpacing: '-0.045em', lineHeight: 1.05 }}
          >
            The quiet center of your professional world.
          </h1>
          <p className="text-base md:text-lg mt-6 max-w-md login-hero-reveal login-hero-reveal-d3" style={{ color: 'var(--pd-muted)', lineHeight: 1.75 }}>
            Every venture, one calm surface — tasks, calls, payments, reading and time, orchestrated daily.
          </p>
        </div>

        <div className="relative z-10 hidden lg:flex items-center gap-2 text-[11px] login-hero-reveal login-hero-reveal-d3" style={{ color: 'var(--pd-subtle)' }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--pd-brand, var(--pd-focus))' }} />
          Command bar · ⌘K
        </div>
      </div>

      {/* ── Right · auth panel ── */}
      <div
        className="relative w-full lg:w-[40%] flex items-center justify-center px-5 py-10 md:px-10 lg:min-h-screen"
        style={{ background: 'var(--pd-panel-solid)', borderLeft: '1px solid var(--pd-border-subtle)' }}
      >
        <button
          data-testid="auth-theme-toggle-button"
          type="button"
          aria-label={theme === 'night' ? 'Switch to light theme' : 'Switch to dark theme'}
          title={theme === 'night' ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={toggleTheme}
          className="pd-icon-button pd-btn-press absolute top-6 right-6 md:top-8 md:right-8"
        >
          {theme === 'night' ? <SunMedium className="w-4.5 h-4.5" /> : <MoonStar className="w-4.5 h-4.5" />}
        </button>

        <div className="w-full max-w-sm login-card-float">
          <h2 className="text-[32px] font-medium" style={{ color: 'var(--pd-text)', letterSpacing: '-0.04em' }}>
            {isLogin ? 'Sign in' : 'Create account'}
          </h2>
          <p className="text-[13px] mt-2 mb-8" style={{ color: 'var(--pd-muted)' }}>
            {isLogin ? 'Pick up where you left off.' : 'Set up your workspace in a few steps.'}
          </p>

          {microsoftEnabled ? (
            <>
              <button
                data-testid="microsoft-login-btn"
                onClick={handleMicrosoftLogin}
                disabled={msLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-[14px] font-medium pd-btn-press transition-colors"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--pd-border)',
                  color: 'var(--pd-text)',
                  opacity: msLoading ? 0.7 : 1,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                </svg>
                {msLoading ? 'Connecting...' : 'Continue with Microsoft'}
              </button>
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px" style={{ background: 'var(--pd-border-subtle)' }} />
                <span className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--pd-subtle)' }}>or</span>
                <div className="flex-1 h-px" style={{ background: 'var(--pd-border-subtle)' }} />
              </div>
            </>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin ? (
              <div>
                <label className="text-[13px] font-medium mb-1.5 block" style={{ color: 'var(--pd-muted)' }}>Full name</label>
                <input data-testid="auth-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full px-4 py-3.5 rounded-xl text-[14px] outline-none pd-input-focus" style={{ background: 'var(--pd-bg)', border: '1px solid var(--pd-border-subtle)', color: 'var(--pd-text)' }} required={!isLogin} />
              </div>
            ) : null}
            <div>
              <label className="text-[13px] font-medium mb-1.5 block" style={{ color: 'var(--pd-muted)' }}>Email</label>
              <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@polkdesk.com" className="w-full px-4 py-3.5 rounded-xl text-[14px] outline-none pd-input-focus" style={{ background: 'var(--pd-bg)', border: '1px solid var(--pd-border-subtle)', color: 'var(--pd-text)' }} required />
            </div>
            <div>
              <label className="text-[13px] font-medium mb-1.5 block" style={{ color: 'var(--pd-muted)' }}>Password</label>
              <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 characters" className="w-full px-4 py-3.5 rounded-xl text-[14px] outline-none pd-input-focus" style={{ background: 'var(--pd-bg)', border: '1px solid var(--pd-border-subtle)', color: 'var(--pd-text)' }} required />
            </div>

            {error ? (
              <div data-testid="auth-error-message" className="text-[13px] px-4 py-3 rounded-xl" style={{ background: 'var(--pd-crimson-soft)', color: 'var(--pd-crimson)', border: '1px solid var(--pd-border-subtle)' }}>
                {error}
              </div>
            ) : null}

            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-[14px] font-medium pd-btn-press transition-opacity"
              style={{ background: 'var(--pd-text)', color: 'var(--pd-bg)', opacity: loading ? 0.75 : 1 }}
            >
              {loading ? (
                <span className="inline-block w-4 h-4 rounded-full animate-spin" style={{ border: '2px solid transparent', borderTopColor: 'var(--pd-bg)', borderRightColor: 'var(--pd-bg)' }} />
              ) : null}
              {loading ? 'Please wait...' : isLogin ? 'Sign in' : 'Create account'}
              {!loading ? <ArrowRight className="w-4 h-4" /> : null}
            </button>
          </form>

          <button
            data-testid="auth-switch-mode-link"
            onClick={() => { setIsLogin(!isLogin); setError(''); }}
            className="w-full text-center text-[13px] mt-8 transition-colors font-medium"
            style={{ color: 'var(--pd-muted)' }}
          >
            {isLogin ? 'Need an account? Create one' : 'Already have one? Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}
